import React from 'react';

interface EngineOutput {
  account_label: string;
  risk_score: number | null;
  position_coverage_pct: number | null;
  n_positions: number;
  n_underlyings?: number;
  flag_count: number;
  flag_severity_summary: string;
}

interface FlagRow {
  flag: string;
  value: string | null;
  threshold: string | null;
  severity: string;
}

interface Props {
  engineOutput: EngineOutput;
  flagsTable: FlagRow[];
  accountLabel?: string;
}

const EngineOutputHeader: React.FC<Props> = ({ engineOutput, flagsTable }) => {
  const { risk_score, position_coverage_pct, n_positions, n_underlyings, flag_count, flag_severity_summary } = engineOutput;
  const lineItemsLabel = `${n_positions} line item${n_positions === 1 ? '' : 's'}`;
  const underlyingsLabel = n_underlyings != null
    ? ` in ${n_underlyings} underlying${n_underlyings === 1 ? '' : 's'}`
    : '';

  return (
    <section className="engine-output-section">
      <h3 className="engine-output-title">Risk engine output</h3>
      <p className="engine-output-score">
        <strong>Risk score: {risk_score != null ? Math.round(risk_score) : '—'} / 100</strong>
        {' '}
        <span className="engine-output-meta">
          ({position_coverage_pct != null ? `${position_coverage_pct.toFixed(0)}% coverage` : 'coverage unknown'}, {lineItemsLabel}{underlyingsLabel}
          {' '}
          <span
            className="chart-help"
            title={
              "A 'line item' is one unique holding — a stock position in AMZN is 1 line item; an AMZN call spread is 2 line items (long call + short call); each option strike or expiration counts separately. 'Underlying' groups these by ticker: AMZN stock + AMZN calls + AMZN puts all roll up to 1 underlying (AMZN)."
            }
          >?</span>)
        </span>
      </p>
      {flag_count > 0 && (
        <>
          <p className="engine-output-flag-summary">{flag_severity_summary}:</p>
          <div className="engine-output-flags-wrap">
            <table className="engine-output-flags">
              <thead>
                <tr>
                  <th>Flag</th>
                  <th className="num">Value</th>
                  <th className="num">Threshold</th>
                </tr>
              </thead>
              <tbody>
                {flagsTable.map((f, i) => (
                  <tr key={i} className={`sev-${f.severity}`}>
                    <td>{f.flag}</td>
                    <td className="num">{f.value ?? '—'}</td>
                    <td className="num">{f.threshold ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {flag_count === 0 && (
        <p className="engine-output-flag-summary">{flag_severity_summary}.</p>
      )}
    </section>
  );
};

export default EngineOutputHeader;
